/**
 * iter162 · Admin Content-Management Sections
 * ─────────────────────────────────────────────
 * ListingsAdminSection: Artikel-Verwaltung (STX ↔ Marketplace umschaltbar)
 * PostsAdminSection:    Beitrags-Verwaltung (Beiträge ↔ Stories umschaltbar)
 *
 * Beide Sections orientieren sich am Layout der bestehenden User-Verwaltung:
 * Header mit Tabs → Suchleiste + Filter → sortierbare Tabelle mit Vorschaubild,
 * "Weiterleiten"-Button + "Löschen mit Grund"-Modal (schickt Notifikation an User).
 */
import { useState, useEffect, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import { Search, Trash2, ExternalLink, ChevronUp, ChevronDown, X, Loader2, User, CheckSquare, Square } from "lucide-react";
// iter165 · Bulk-Delete Support

const DIR_ARROW = (dir) => (dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />);

function SortableHeader({ label, field, sortBy, sortDir, onSort, testId }) {
  const active = sortBy === field;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1 hover:text-[#0A0A0A] transition-colors ${active ? "text-[#0A0A0A] font-bold" : "text-[#666]"}`}
    >
      {label} {active ? DIR_ARROW(sortDir) : null}
    </button>
  );
}

function SexBadge({ sex }) {
  if (!sex) return <span className="text-[10px] text-[#CCC]">—</span>;
  const map = { MALE: "M", FEMALE: "W", DIVERSE: "D" };
  const color = { MALE: "bg-blue-100 text-blue-700", FEMALE: "bg-pink-100 text-pink-700", DIVERSE: "bg-purple-100 text-purple-700" };
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${color[sex] || "bg-[#F5F5F5] text-[#999]"}`}>
      {map[sex] || "?"}
    </span>
  );
}

function DeleteReasonModal({ open, onClose, onConfirm, title }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setReason(""); setErr(""); } }, [open]);
  if (!open) return null;
  const submit = async () => {
    const r = reason.trim();
    if (r.length < 3) { setErr("Löschgrund muss mindestens 3 Zeichen enthalten."); return; }
    setBusy(true); setErr("");
    try { await onConfirm(r); } catch (e) { setErr(formatError(e) || "Fehler beim Löschen"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center bg-black/60 p-4" data-testid="delete-reason-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-bold text-[#0A0A0A]">{title || "Löschen mit Begründung"}</h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#0A0A0A]" data-testid="delete-reason-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="text-[10px] uppercase tracking-wider text-[#999] font-bold block">Grund für die Löschung</label>
          <textarea
            data-testid="delete-reason-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={500}
            placeholder="Bitte begründe, warum dieser Inhalt entfernt wird (wird dem User als Benachrichtigung geschickt)…"
            className="w-full border border-[#E5E5E5] rounded-lg p-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          />
          <p className="text-[10px] text-[#999]">{reason.length}/500 · Der User erhält die Nachricht direkt in seinen Benachrichtigungen.</p>
          {err && <p className="text-xs text-red-500" data-testid="delete-reason-error">{err}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#E5E5E5]">
          <button onClick={onClose} disabled={busy} className="h-9 px-4 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium hover:bg-[#F5F5F5]" data-testid="delete-reason-cancel">Abbrechen</button>
          <button onClick={submit} disabled={busy || reason.trim().length < 3} className="h-9 px-4 rounded-full bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="delete-reason-confirm">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Endgültig löschen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────── Listings ────────────────────────────── */

export function ListingsAdminSection() {
  const [scope, setScope] = useState("STILLTOUCHEDX"); // STILLTOUCHEDX | MARKETPLACE
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  // iter165 · Bulk-Select
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  // iter171 · Lagerbestand-Filter
  const [stockFilter, setStockFilter] = useState(""); // "" | IN_STOCK | LOW_STOCK | OUT_OF_STOCK

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const params = new URLSearchParams({
        scope, sort_by: sortBy, sort_dir: sortDir, page: String(page), limit: "25",
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(stockFilter ? { stock_filter: stockFilter } : {}),
      });
      const { data } = await api.get(`/admin/listings?${params.toString()}`);
      setItems(data.listings || []);
      setTotal(data.total || 0);
    } catch (e) { setErr(formatError(e) || "Ladefehler"); }
    finally { setBusy(false); }
  }, [scope, search, sortBy, sortDir, page, stockFilter]);

  useEffect(() => { setPage(1); }, [scope, search, stockFilter]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir("desc"); }
  };

  const confirmDelete = async (reason) => {
    if (!deleteTarget) return;
    await api.delete(`/admin/listings/${deleteTarget.listing_id}`, { data: { reason } });
    setDeleteTarget(null);
    load();
  };

  return (
    <div className="max-w-7xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#0A0A0A]">Artikel-Verwaltung</h2>
          <p className="text-xs text-[#999]">Alle Artikel im gewählten Bereich · Suche, Sortierung und Löschung mit User-Benachrichtigung.</p>
        </div>
        <div className="flex items-center gap-2" role="tablist" data-testid="listings-admin-scope-tabs">
          {[["STILLTOUCHEDX", "stilltouchedX"], ["MARKETPLACE", "Marketplace"]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`listings-admin-scope-${k.toLowerCase()}`}
              onClick={() => setScope(k)}
              className={`h-9 px-4 rounded-full text-xs font-medium transition ${scope === k ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5]"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <input
            data-testid="listings-admin-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Artikelname, Artikelnummer (MP-000019), Seller-Name oder Handle…"
            className="w-full h-10 pl-9 pr-3 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          />
        </div>
        <p className="text-xs text-[#999]" data-testid="listings-admin-total">{total} Artikel</p>
        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-xs text-red-700" data-testid="listings-admin-bulk-bar">
            <span>{selected.size} ausgewählt</span>
            <button
              onClick={() => setBulkOpen(true)}
              data-testid="listings-admin-bulk-delete-btn"
              className="h-7 px-3 rounded-full bg-red-600 text-white text-[10px] font-medium hover:bg-red-700 inline-flex items-center gap-1"
            >
              <Trash2 size={10} /> Alle löschen
            </button>
            <button onClick={() => setSelected(new Set())} className="text-[10px] text-red-500 underline" data-testid="listings-admin-bulk-clear">leeren</button>
          </div>
        )}
      </div>

      {/* iter171 · Lagerbestand-Filter */}
      <div className="flex items-center gap-2 mb-3" data-testid="listings-admin-stock-filter">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Bestand:</span>
        {[
          ["", "Alle"],
          ["IN_STOCK", "Verfügbar (>3)"],
          ["LOW_STOCK", "Wenige (1–3)"],
          ["OUT_OF_STOCK", "Ausverkauft (0)"],
        ].map(([k, label]) => (
          <button
            key={k || "all"}
            data-testid={`listings-admin-stock-${k.toLowerCase() || "all"}`}
            onClick={() => setStockFilter(k)}
            className={`h-8 px-3 rounded-full text-[11px] font-medium transition ${stockFilter === k ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5]"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {err && <p className="text-xs text-red-500 mb-2" data-testid="listings-admin-error">{err}</p>}

      <div className="border border-[#E5E5E5] rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider">
            <tr className="border-b border-[#E5E5E5]">
              <th className="text-left px-2 py-2 w-10">
                <button
                  data-testid="listings-admin-select-all"
                  onClick={() => {
                    setSelected((prev) => {
                      const idsOnPage = items.map((x) => x.listing_id);
                      const allSelected = idsOnPage.every((id) => prev.has(id));
                      const next = new Set(prev);
                      if (allSelected) idsOnPage.forEach((id) => next.delete(id));
                      else idsOnPage.forEach((id) => next.add(id));
                      return next;
                    });
                  }}
                  className="text-[#666] hover:text-brand-black"
                  title="Alle auf dieser Seite an-/abwählen"
                >
                  {items.length > 0 && items.every((x) => selected.has(x.listing_id)) ? <CheckSquare size={14} /> : <Square size={14} />}
                </button>
              </th>
              <th className="text-left px-3 py-2 w-14">Bild</th>
              <th className="text-left px-3 py-2"><SortableHeader label="Artikel" field="title" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-title" /></th>
              <th className="text-left px-3 py-2 w-40"><SortableHeader label="Verkäufer" field="seller_display_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-seller" /></th>
              <th className="text-left px-3 py-2 w-16">Sex</th>
              <th className="text-left px-3 py-2 w-24"><SortableHeader label="Preis" field="price" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-price" /></th>
              <th className="text-left px-3 py-2 w-20"><SortableHeader label="Bestand" field="quantity" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-quantity" /></th>
              <th className="text-left px-3 py-2 w-24"><SortableHeader label="Beobachter" field="watch_count" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-watch" /></th>
              <th className="text-left px-3 py-2 w-28"><SortableHeader label="Status" field="listing_status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-status" /></th>
              <th className="text-right px-3 py-2 w-32">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {busy && items.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[#999] text-xs">Lade…</td></tr>
            )}
            {!busy && items.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[#999] text-xs" data-testid="listings-admin-empty">Keine Artikel gefunden.</td></tr>
            )}
            {items.map((l) => {
              const isSold = l.listing_status === "SOLD" || l.listing_status === "SOLD_OUT" || (l.quantity ?? 0) <= 0;
              const isSel = selected.has(l.listing_id);
              return (
                <tr key={l.listing_id} className={`border-b border-[#F0F0F0] hover:bg-[#FAFAFA] ${isSel ? "bg-red-50/40" : ""}`} data-testid={`listings-admin-row-${l.listing_id}`}>
                  <td className="px-2 py-2">
                    <button
                      data-testid={`listings-admin-select-${l.listing_id}`}
                      onClick={() => setSelected((prev) => { const n = new Set(prev); if (n.has(l.listing_id)) n.delete(l.listing_id); else n.add(l.listing_id); return n; })}
                      className="text-[#666] hover:text-brand-black"
                    >
                      {isSel ? <CheckSquare size={14} /> : <Square size={14} />}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    {l.thumbnail_url ? (
                      <img src={l.thumbnail_url} alt="" className="w-10 h-10 rounded object-cover" loading="lazy" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-[#F0F0F0] flex items-center justify-center text-[9px] text-[#999]">—</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-[#0A0A0A] truncate max-w-[240px]" data-testid={`listings-admin-title-${l.listing_id}`}>{l.title || "—"}</p>
                    <p className="text-[10px] text-[#999]">#{l.public_item_no || l.listing_id.slice(0, 8)}</p>
                  </td>
                  <td className="px-3 py-2" data-testid={`listings-admin-seller-${l.listing_id}`}>
                    <p className="text-[11px] font-medium text-[#0A0A0A] truncate max-w-[150px]">{l.seller_display_name || "—"}</p>
                    <p className="text-[10px] text-[#999] truncate max-w-[150px]">@{l.seller_handle || "?"}</p>
                  </td>
                  <td className="px-3 py-2"><SexBadge sex={l.seller_sex} /></td>
                  <td className="px-3 py-2">{l.price != null ? `${Number(l.price).toFixed(2)} ${l.currency || "€"}` : "—"}</td>
                  <td className="px-3 py-2" data-testid={`listings-admin-stock-${l.listing_id}`}>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      (l.quantity ?? 0) <= 0
                        ? "bg-red-100 text-red-700"
                        : (l.quantity ?? 0) <= 3
                        ? "bg-amber-100 text-amber-800"
                        : "bg-green-100 text-green-700"
                    }`}>
                      {l.quantity ?? 0}
                    </span>
                  </td>
                  <td className="px-3 py-2">{l.watch_count ?? 0}</td>
                  <td className="px-3 py-2">
                    {l.listing_status === "DELETED" ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-medium">Gelöscht</span>
                    ) : isSold ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[10px] font-medium">Ausverkauft</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-medium">Verfügbar</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`listings-admin-open-${l.listing_id}`}
                        onClick={() => {
                          // iter164 · korrekte Route je Bereich
                          const path = l.area_scope === "STILLTOUCHEDX"
                            ? `/stilltouchedx/${l.listing_id}`
                            : `/marketplace/${l.listing_id}`;
                          window.open(path, "_blank", "noopener,noreferrer");
                        }}
                        title="Im Frontend öffnen (neuer Tab)"
                        className="w-8 h-8 rounded-full border border-[#E5E5E5] hover:bg-[#F5F5F5] inline-flex items-center justify-center text-[#666]"
                      >
                        <ExternalLink size={12} />
                      </button>
                      <button
                        data-testid={`listings-admin-delete-${l.listing_id}`}
                        onClick={() => setDeleteTarget(l)}
                        disabled={l.listing_status === "DELETED"}
                        title="Artikel löschen (mit Begründung)"
                        className="w-8 h-8 rounded-full border border-red-300 text-red-500 hover:bg-red-50 inline-flex items-center justify-center disabled:opacity-40"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-[#666]">
        <button
          data-testid="listings-admin-prev"
          disabled={page <= 1 || busy}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="h-8 px-3 rounded-full border border-[#E5E5E5] disabled:opacity-40 hover:bg-[#F5F5F5]"
        >← Zurück</button>
        <p data-testid="listings-admin-page">Seite {page} · {Math.ceil(total / 25) || 1}</p>
        <button
          data-testid="listings-admin-next"
          disabled={page * 25 >= total || busy}
          onClick={() => setPage((p) => p + 1)}
          className="h-8 px-3 rounded-full border border-[#E5E5E5] disabled:opacity-40 hover:bg-[#F5F5F5]"
        >Weiter →</button>
      </div>

      <DeleteReasonModal
        open={!!deleteTarget}
        title={deleteTarget ? `Artikel „${deleteTarget.title}" löschen?` : ""}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <DeleteReasonModal
        open={bulkOpen}
        title={`${selected.size} Artikel löschen?`}
        onClose={() => setBulkOpen(false)}
        onConfirm={async (reason) => {
          const { data } = await api.post("/admin/listings/bulk-delete", { ids: Array.from(selected), reason });
          setBulkOpen(false);
          setSelected(new Set());
          load();
          return data;
        }}
      />
    </div>
  );
}

/* ────────────────────────────── Posts + Stories ────────────────────────────── */

export function PostsAdminSection() {
  const [contentType, setContentType] = useState("POST");
  const [areaScope, setAreaScope] = useState("SOCIAL"); // SOCIAL | STILLTOUCHEDX (nur für POST)
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  // iter165 · Bulk-Select
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const params = new URLSearchParams({
        content_type: contentType, sort_by: sortBy, sort_dir: sortDir, page: String(page), limit: "25",
        ...(contentType === "POST" ? { area_scope: areaScope } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      });
      const { data } = await api.get(`/admin/social-content?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { setErr(formatError(e) || "Ladefehler"); }
    finally { setBusy(false); }
  }, [contentType, areaScope, search, sortBy, sortDir, page]);

  useEffect(() => { setPage(1); }, [contentType, areaScope, search]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir("desc"); }
  };

  const confirmDelete = async (reason) => {
    if (!deleteTarget) return;
    await api.delete(
      `/admin/social-content/${deleteTarget.content_id}?content_type=${deleteTarget.content_type}`,
      { data: { reason } },
    );
    setDeleteTarget(null);
    load();
  };

  const openInFrontend = (item) => {
    // iter164 · Zum konkreten Beitrag/Story springen (nicht mehr auf das Profil).
    if (item.content_type === "STORY") {
      window.open(`/stories/${item.author_id}?story=${item.content_id}`, "_blank", "noopener,noreferrer");
    } else if (item.author_handle) {
      // PostDetailPage ist iter160 entfernt → PublicProfilePage highlightet Post via ?post=<id>
      window.open(`/user/${item.author_handle}?post=${item.content_id}#post-${item.content_id}`, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="max-w-7xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#0A0A0A]">Beitrags-Verwaltung</h2>
          <p className="text-xs text-[#999]">Alle Social-Beiträge und Stories · Suche, Sortierung und Löschung mit User-Benachrichtigung.</p>
        </div>
        <div className="flex items-center gap-2" role="tablist" data-testid="posts-admin-type-tabs">
          {[["POST", "Beiträge"], ["STORY", "Stories"]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`posts-admin-type-${k.toLowerCase()}`}
              onClick={() => setContentType(k)}
              className={`h-9 px-4 rounded-full text-xs font-medium transition ${contentType === k ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5]"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Social ↔ stilltouchedX (nur Beiträge; Stories gibt es nur im Social-Bereich) */}
      {contentType === "POST" && (
        <div className="flex items-center gap-2 mb-3" role="tablist" data-testid="posts-admin-area-tabs">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Bereich:</span>
          {[["SOCIAL", "Social (Feed)"], ["STILLTOUCHEDX", "stilltouchedX (Feed)"]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`posts-admin-area-${k.toLowerCase()}`}
              onClick={() => setAreaScope(k)}
              className={`h-8 px-3 rounded-full text-[11px] font-medium transition ${areaScope === k ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5]"}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <input
            data-testid="posts-admin-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Text, Beschreibung, Autor…"
            className="w-full h-10 pl-9 pr-3 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          />
        </div>
        <p className="text-xs text-[#999]" data-testid="posts-admin-total">{total} Einträge</p>
        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-xs text-red-700" data-testid="posts-admin-bulk-bar">
            <span>{selected.size} ausgewählt</span>
            <button
              onClick={() => setBulkOpen(true)}
              data-testid="posts-admin-bulk-delete-btn"
              className="h-7 px-3 rounded-full bg-red-600 text-white text-[10px] font-medium hover:bg-red-700 inline-flex items-center gap-1"
            >
              <Trash2 size={10} /> Alle löschen
            </button>
            <button onClick={() => setSelected(new Set())} className="text-[10px] text-red-500 underline" data-testid="posts-admin-bulk-clear">leeren</button>
          </div>
        )}
      </div>

      {err && <p className="text-xs text-red-500 mb-2" data-testid="posts-admin-error">{err}</p>}

      <div className="border border-[#E5E5E5] rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider">
            <tr className="border-b border-[#E5E5E5]">
              <th className="text-left px-2 py-2 w-10">
                <button
                  data-testid="posts-admin-select-all"
                  onClick={() => {
                    setSelected((prev) => {
                      const idsOnPage = items.map((x) => x.content_id);
                      const allSelected = idsOnPage.every((id) => prev.has(id));
                      const next = new Set(prev);
                      if (allSelected) idsOnPage.forEach((id) => next.delete(id));
                      else idsOnPage.forEach((id) => next.add(id));
                      return next;
                    });
                  }}
                  className="text-[#666] hover:text-brand-black"
                  title="Alle auf dieser Seite an-/abwählen"
                >
                  {items.length > 0 && items.every((x) => selected.has(x.content_id)) ? <CheckSquare size={14} /> : <Square size={14} />}
                </button>
              </th>
              <th className="text-left px-3 py-2 w-14">Avatar</th>
              <th className="text-left px-3 py-2 w-44"><SortableHeader label="Autor" field="author_display_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-author" /></th>
              <th className="text-left px-3 py-2"><SortableHeader label="Inhalt" field="title" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-content" /></th>
              <th className="text-left px-3 py-2 w-16">Sex</th>
              <th className="text-left px-3 py-2 w-20"><SortableHeader label="Likes" field="like_count" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-likes" /></th>
              <th className="text-left px-3 py-2 w-24"><SortableHeader label="Kommentare" field="comment_count" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-comments" /></th>
              <th className="text-left px-3 py-2 w-28"><SortableHeader label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} testId="sort-status2" /></th>
              <th className="text-right px-3 py-2 w-32">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {busy && items.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-[#999] text-xs">Lade…</td></tr>
            )}
            {!busy && items.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-[#999] text-xs" data-testid="posts-admin-empty">Nichts gefunden.</td></tr>
            )}
            {items.map((p) => {
              const isDeleted = String(p.status || "").startsWith("DELETED");
              const isSel = selected.has(p.content_id);
              return (
                <tr key={p.content_id} className={`border-b border-[#F0F0F0] hover:bg-[#FAFAFA] ${isSel ? "bg-red-50/40" : ""}`} data-testid={`posts-admin-row-${p.content_id}`}>
                  <td className="px-2 py-2">
                    <button
                      data-testid={`posts-admin-select-${p.content_id}`}
                      onClick={() => setSelected((prev) => { const n = new Set(prev); if (n.has(p.content_id)) n.delete(p.content_id); else n.add(p.content_id); return n; })}
                      className="text-[#666] hover:text-brand-black"
                    >
                      {isSel ? <CheckSquare size={14} /> : <Square size={14} />}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    {p.author_avatar_url ? (
                      <img src={p.author_avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#F0F0F0] flex items-center justify-center text-[#999]"><User size={14} /></div>
                    )}
                  </td>
                  <td className="px-3 py-2" data-testid={`posts-admin-author-${p.content_id}`}>
                    <p className="text-[11px] font-medium text-[#0A0A0A] truncate max-w-[160px]">{p.author_display_name || (p.author_type === "PLATFORM" ? "stilltouched" : "—")}</p>
                    <p className="text-[10px] text-[#999] truncate max-w-[160px]">@{p.author_handle || (p.author_type === "PLATFORM" ? "stilltouched" : "?")}</p>
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-[#0A0A0A] truncate max-w-[380px]" data-testid={`posts-admin-excerpt-${p.content_id}`}>{p.excerpt || <span className="text-[#CCC]">(kein Text)</span>}</p>
                    <p className="text-[10px] text-[#999]">{p.author_type === "PLATFORM" ? "Platform-Post" : (p.media_type === "VIDEO" ? "Video" : (p.media_url ? "Bild" : "Text"))}</p>
                  </td>
                  <td className="px-3 py-2"><SexBadge sex={p.author_sex} /></td>
                  <td className="px-3 py-2">{p.like_count ?? 0}</td>
                  <td className="px-3 py-2">{p.comment_count ?? 0}</td>
                  <td className="px-3 py-2">
                    {isDeleted ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-medium">Gelöscht</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-medium">Aktiv</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`posts-admin-open-${p.content_id}`}
                        onClick={() => openInFrontend(p)}
                        title="Im Frontend öffnen"
                        className="w-8 h-8 rounded-full border border-[#E5E5E5] hover:bg-[#F5F5F5] inline-flex items-center justify-center text-[#666]"
                      >
                        <ExternalLink size={12} />
                      </button>
                      <button
                        data-testid={`posts-admin-delete-${p.content_id}`}
                        onClick={() => setDeleteTarget(p)}
                        disabled={isDeleted}
                        title="Löschen (mit Begründung)"
                        className="w-8 h-8 rounded-full border border-red-300 text-red-500 hover:bg-red-50 inline-flex items-center justify-center disabled:opacity-40"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-[#666]">
        <button
          data-testid="posts-admin-prev"
          disabled={page <= 1 || busy}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="h-8 px-3 rounded-full border border-[#E5E5E5] disabled:opacity-40 hover:bg-[#F5F5F5]"
        >← Zurück</button>
        <p data-testid="posts-admin-page">Seite {page} · {Math.ceil(total / 25) || 1}</p>
        <button
          data-testid="posts-admin-next"
          disabled={page * 25 >= total || busy}
          onClick={() => setPage((p) => p + 1)}
          className="h-8 px-3 rounded-full border border-[#E5E5E5] disabled:opacity-40 hover:bg-[#F5F5F5]"
        >Weiter →</button>
      </div>

      <DeleteReasonModal
        open={!!deleteTarget}
        title={deleteTarget ? `${deleteTarget.content_type === "STORY" ? "Story" : "Beitrag"} löschen?` : ""}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <DeleteReasonModal
        open={bulkOpen}
        title={`${selected.size} ${contentType === "STORY" ? "Stories" : "Beiträge"} löschen?`}
        onClose={() => setBulkOpen(false)}
        onConfirm={async (reason) => {
          const { data } = await api.post(`/admin/social-content/bulk-delete?content_type=${contentType}`, { ids: Array.from(selected), reason });
          setBulkOpen(false);
          setSelected(new Set());
          load();
          return data;
        }}
      />
    </div>
  );
}
